import React from "react";
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Person } from "@mui/icons-material";
import { ListBulletIcon } from "@radix-ui/react-icons";
import { Trash2 } from "lucide-react";
import { PageHeader } from "../../shared/components/PageHeader";
import { EmptyState } from "../../shared/components/EmptyState";

function UsersView({
  darkMode,
  theme,
  users,
  loadingUsers,
  isMobile,
  fetchSessionLogs,
  setOpenUserDialog,
  deleteUser,
}) {
  return (
    <Fade in={true} timeout={500}>
      <Box>
        <PageHeader
          title="User Management"
          subtitle="Manage system users and permissions"
          darkMode={darkMode}
          actions={
            <>
              <Button
                variant="outlined"
                startIcon={<ListBulletIcon />}
                onClick={fetchSessionLogs}
              >
                View Logs
              </Button>
              <Button
                variant="contained"
                startIcon={<Person />}
                onClick={() => setOpenUserDialog(true)}
              >
                Add User
              </Button>
            </>
          }
        />

        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 2 }}>
              {users.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    bgcolor: darkMode ? '#16213A' : '#EAF3FB',
                    border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: user.username === 'admin' ? 'primary.main' : 'secondary.main' }}>
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }} noWrap>
                        {user.username}
                      </Typography>
                      <Chip
                        size="small"
                        label={user.username === 'admin' ? 'Administrator' : 'User'}
                        sx={{
                          mt: 0.5,
                          bgcolor: user.username === 'admin' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.secondary.main, 0.1),
                          color: user.username === 'admin' ? 'primary.main' : 'secondary.main',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                  {user.username !== "admin" && (
                    <IconButton color="error" onClick={() => deleteUser(user.id)} size="small">
                      <Trash2 size={18} />
                    </IconButton>
                  )}
                </Box>
              ))}
              {users.length === 0 && (
                <EmptyState darkMode={darkMode} title="No users found" subtitle="Add a new user to get started" />
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <TableContainer>
              <Table sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                          {user.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: user.username === 'admin' ? 'primary.main' : 'secondary.main' }}>
                            {user.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                            {user.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={user.username === 'admin' ? 'Administrator' : 'User'}
                          sx={{
                            bgcolor: user.username === 'admin' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.secondary.main, 0.1),
                            color: user.username === 'admin' ? 'primary.main' : 'secondary.main',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {user.username !== "admin" && (
                          <Tooltip title="Delete User">
                            <IconButton
                              color="error"
                              onClick={() => deleteUser(user.id)}
                              size="small"
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {users.length === 0 && (
              <EmptyState darkMode={darkMode} title="No users found" subtitle="Add a new user to get started" />
            )}
          </Card>
        )}
      </Box>
    </Fade>
  );
}

export default UsersView;
